import { Bottle, CreateBottleDto, UpdateBottleDto, ArchiveBottleDto } from '../../domain/entities';
import { API_CONFIG } from '../../core/config/constants';
import { apiClient } from './axios-config';
import { BackendResponse, BackendBouteille, BouteilleDTO, BottlesListResponse, BOTTLE_TYPE_REVERSE_MAPPING } from '../models/backend-models';
import { mapBackendBottleToFrontend, mapFrontendBottleToBackend } from '../mappers/bottle-mapper';

// Utilitaire pour convertir un File en Base64 (comme dans l'app mobile)
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Garder seulement la partie Base64, sans le préfixe data:mime/type;base64,
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Erreur lors de la conversion en Base64'));
      }
    };
    reader.onerror = error => reject(error);
  });
};

export interface BottlesFilter {
  type?: string;
  isArchived?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  totalPages: number;
}

export const bottlesApi = {
  getBottles: async (filter?: BottlesFilter): Promise<PaginatedResponse<Bottle>> => {
    console.log('🌐 API - getBottles appelé avec filtres:', filter);
    console.log('🌐 API - URL complète:', `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BOTTLES.LIST}`);
    
    try {
      console.log('🌐 API - Avant appel API...');
      const response = await apiClient.get<BackendResponse<BottlesListResponse>>(API_CONFIG.ENDPOINTS.BOTTLES.LIST);
      console.log('🌐 API - Response complète:', response);
      console.log('🌐 API - Response status HTTP:', response.status);
      console.log('🌐 API - Response data:', response.data);
      console.log('🌐 API - Type de response.data:', typeof response.data);
      console.log('🌐 API - Response data structure:', JSON.stringify(response.data, null, 2));
      console.log('🌐 API - Keys of response.data:', Object.keys(response.data));
      console.log('🌐 API - Checking if response.data is array:', Array.isArray(response.data));
      
      const data = response.data;
      
      if (!data) {
        console.error('🌐 API - Pas de données dans la réponse');
        throw new Error('Aucune donnée reçue du serveur');
      }
      
      console.log('🌐 API - Status de la réponse:', data.status);
      console.log('🌐 API - Vérification content:', !!data.content);
      console.log('🌐 API - Type de content:', typeof data.content);
      console.log('🌐 API - Content bouteilles:', data.content?.bouteilles?.length || 0);
      
      // Gérer le format de réponse imbriqué
      let bottlesArray: BackendBouteille[] = [];
      
      // Format attendu: BackendResponse<BottlesListResponse>
      if (data && typeof data === 'object' && 'content' in data && (data.status === '200' || data.status === 200)) {
        const bottlesResponse = data.content;
        
        if (bottlesResponse && typeof bottlesResponse === 'object' && 'bouteilles' in bottlesResponse && bottlesResponse.status === 'OK') {
          if (!Array.isArray(bottlesResponse.bouteilles)) {
            console.warn('🌐 API - data.content.bouteilles n\'est pas un tableau:', bottlesResponse.bouteilles);
            bottlesArray = [];
          } else {
            bottlesArray = bottlesResponse.bouteilles;
          }
          console.log('🌐 API - Format BackendResponse<BottlesListResponse> détecté');
          console.log('🌐 API - userName:', bottlesResponse.userName);
        } else if (bottlesResponse && typeof bottlesResponse === 'object' && 'bouteilles' in bottlesResponse) {
          // Gérer le cas où status n'est pas 'OK' mais la structure est correcte
          if (!Array.isArray(bottlesResponse.bouteilles)) {
            console.warn('🌐 API - data.content.bouteilles n\'est pas un tableau:', bottlesResponse.bouteilles);
            bottlesArray = [];
          } else {
            bottlesArray = bottlesResponse.bouteilles || [];
          }
          console.log('🌐 API - Format BackendResponse<BottlesListResponse> détecté (status non-OK mais structure valide)');
        } else {
          // Cas où il n'y a pas de bouteilles du tout - retourner un tableau vide au lieu d'une erreur
          console.log('🌐 API - Aucune bouteille trouvée ou format non standard, retour d\'un tableau vide');
          bottlesArray = [];
        }
      }
      // Cas de fallback - retourner un tableau vide au lieu d'une erreur
      else {
        console.log('🌐 API - Format de réponse non standard, retour d\'un tableau vide. Data:', data);
        bottlesArray = [];
      }
      
      console.log('🌐 API - Nombre de bouteilles reçues:', bottlesArray.length);
      console.log('🌐 API - Première bouteille (exemple):', bottlesArray[0]);
      
      // Mapper les bouteilles du backend vers le frontend
      const mappedBottles = bottlesArray.map((bottle, index) => {
          console.log(`🌐 API - Mapping bouteille ${index + 1}:`, bottle);
          try {
            const mapped = mapBackendBottleToFrontend(bottle);
            console.log(`🌐 API - Bouteille ${index + 1} mappée:`, mapped);
            return mapped;
          } catch (mapError) {
            console.error(`🌐 API - Erreur mapping bouteille ${index + 1}:`, mapError);
            throw mapError;
          }
        });
        
        console.log('🌐 API - Toutes les bouteilles mappées:', mappedBottles);
        
        // Filtrer selon les critères si nécessaires
        let filteredBottles = mappedBottles;
        
        if (filter?.type && filter.type !== '') {
          filteredBottles = filteredBottles.filter(bottle => bottle.type === filter.type);
        }
        
        if (filter?.search && filter.search !== '') {
          const searchLower = filter.search.toLowerCase();
          filteredBottles = filteredBottles.filter(bottle => 
            bottle.name.toLowerCase().includes(searchLower) ||
            (bottle.description && bottle.description.toLowerCase().includes(searchLower))
          );
        }
        
        const paginatedData = {
          data: filteredBottles,
          total: filteredBottles.length,
          page: 1,
          totalPages: 1
        };
        
        console.log('🌐 API - Données finales:', paginatedData);
        return paginatedData;
    } catch (error: any) {
      console.error('🌐 API - Erreur complète getBottles:', error);
      console.error('🌐 API - Error name:', error.name);
      console.error('🌐 API - Error message:', error.message);
      console.error('🌐 API - Error response:', error.response);
      console.error('🌐 API - Error response status:', error.response?.status);
      console.error('🌐 API - Error response data:', error.response?.data);
      
      if (error.response) {
        // Erreur de réponse HTTP (4xx, 5xx)
        const statusCode = error.response.status;
        const responseData = error.response.data;
        console.error(`🌐 API - Erreur HTTP ${statusCode}:`, responseData);
        throw new Error(`Erreur serveur (${statusCode}): ${responseData?.message || error.message}`);
      } else if (error.request) {
        // Pas de réponse reçue
        console.error('🌐 API - Pas de réponse du serveur:', error.request);
        throw new Error('Impossible de contacter le serveur');
      } else {
        // Erreur de configuration
        throw error;
      }
    }
  },

  getBottle: async (reference: string): Promise<Bottle> => {
    try {
      const { data } = await apiClient.get<BackendResponse<BackendBouteille>>(API_CONFIG.ENDPOINTS.BOTTLES.GET(reference));
      
      if (data.status === '200' && data.content) {
        return mapBackendBottleToFrontend(data.content);
      } else {
        throw new Error(data.message || 'Bouteille non trouvée');
      }
    } catch (error) {
      console.error('🌐 API - Erreur getBottle:', error);
      throw error;
    }
  },

  findBottleById: async (bouteilleId: number): Promise<Bottle> => {
    try {
      const dto: BouteilleDTO = { bouteilleId };
      const { data } = await apiClient.post<BackendResponse<BackendBouteille>>(API_CONFIG.ENDPOINTS.BOTTLES.FIND, dto);
      
      if (data.status === '200' && data.content) {
        return mapBackendBottleToFrontend(data.content);
      } else {
        throw new Error(data.message || 'Bouteille non trouvée');
      }
    } catch (error) {
      console.error('🌐 API - Erreur findBottleById:', error);
      throw error;
    }
  },

  createBottle: async (bottle: CreateBottleDto): Promise<Bottle> => {
    try {
      const backendData = mapFrontendBottleToBackend(bottle as Bottle);
      const { data } = await apiClient.post<BackendResponse<BackendBouteille>>(API_CONFIG.ENDPOINTS.BOTTLES.CREATE, backendData);
      
      if (data.status === '200' && data.content) {
        return mapBackendBottleToFrontend(data.content);
      } else {
        throw new Error(data.message || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('🌐 API - Erreur createBottle:', error);
      throw error;
    }
  },

  updateBottle: async (reference: string, bottle: UpdateBottleDto): Promise<Bottle> => {
    try {
      const backendData = { ...mapFrontendBottleToBackend(bottle as Bottle), reference };
      const { data } = await apiClient.post<BackendResponse<BackendBouteille>>(API_CONFIG.ENDPOINTS.BOTTLES.UPDATE, backendData);
      
      if (data.status === '200' && data.content) {
        return mapBackendBottleToFrontend(data.content);
      } else {
        throw new Error(data.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('🌐 API - Erreur updateBottle:', error);
      throw error;
    }
  },

  deleteBottle: async (reference: string): Promise<void> => {
    try {
      const dto: BouteilleDTO = { reference };
      const { data } = await apiClient.post<BackendResponse<any>>(API_CONFIG.ENDPOINTS.BOTTLES.DELETE, dto);
      
      if (data.status !== '200') {
        throw new Error(data.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('🌐 API - Erreur deleteBottle:', error);
      throw error;
    }
  },

  archiveBottle: async (reference: string, archiveData: ArchiveBottleDto): Promise<Bottle> => {
    try {
      const dto: BouteilleDTO = { 
        reference,
        commentaire: archiveData.archiveComment 
      };
      const { data } = await apiClient.post<BackendResponse<BackendBouteille>>(API_CONFIG.ENDPOINTS.BOTTLES.ARCHIVE, dto);
      
      if (data.status === '200' && data.content) {
        return mapBackendBottleToFrontend(data.content);
      } else {
        throw new Error(data.message || 'Erreur lors de l\'archivage');
      }
    } catch (error) {
      console.error('🌐 API - Erreur archiveBottle:', error);
      throw error;
    }
  },

  getArchivedBottles: async (filter?: BottlesFilter): Promise<PaginatedResponse<Bottle>> => {
    console.log('🌐 API - getArchivedBottles appelé');
    try {
      const response = await apiClient.post<BackendResponse<BottlesListResponse>>(API_CONFIG.ENDPOINTS.BOTTLES.ARCHIVED, {});
      console.log('🌐 API - Response archives:', response);
      
      const data = response.data;
      
      if (!data) {
        console.error('🌐 API - Pas de données dans la réponse');
        throw new Error('Aucune donnée reçue du serveur');
      }
      
      let bottlesArray: BackendBouteille[] = [];
      
      // Format attendu similaire à getBottles
      if (data && typeof data === 'object' && 'content' in data && (data.status === '200' || data.status === 200)) {
        const bottlesResponse = data.content;
        
        if (bottlesResponse && typeof bottlesResponse === 'object' && 'bouteilles' in bottlesResponse && bottlesResponse.status === 'SUCCESS') {
          if (!Array.isArray(bottlesResponse.bouteilles)) {
            console.warn('🌐 API - data.content.bouteilles n\'est pas un tableau:', bottlesResponse.bouteilles);
            bottlesArray = [];
          } else {
            bottlesArray = bottlesResponse.bouteilles;
          }
          console.log('🌐 API - Nombre de bouteilles archivées:', bottlesArray.length);
        } else {
          console.warn('🌐 API - Format content invalide ou pas de bouteilles archivées:', bottlesResponse);
          bottlesArray = [];
        }
      } else {
        console.warn('🌐 API - Format de réponse non reconnu, retour d\'un tableau vide:', data);
        bottlesArray = [];
      }
      
      // Mapper les bouteilles
      const mappedBottles = bottlesArray.map((bottle, index) => {
        console.log(`🌐 API - Mapping bouteille archivée ${index + 1}:`, bottle);
        try {
          const mapped = mapBackendBottleToFrontend(bottle);
          console.log(`🌐 API - Bouteille archivée ${index + 1} mappée:`, mapped);
          return mapped;
        } catch (mapError) {
          console.error(`🌐 API - Erreur mapping bouteille archivée ${index + 1}:`, mapError);
          throw mapError;
        }
      });
      
      // Filtrer selon les critères si nécessaires
      let filteredBottles = mappedBottles;
      
      if (filter?.type && filter.type !== '') {
        filteredBottles = filteredBottles.filter(bottle => bottle.type === filter.type);
      }
      
      if (filter?.search && filter.search !== '') {
        const searchLower = filter.search.toLowerCase();
        filteredBottles = filteredBottles.filter(bottle => 
          bottle.name.toLowerCase().includes(searchLower) ||
          (bottle.description && bottle.description.toLowerCase().includes(searchLower))
        );
      }
      
      const paginatedData = {
        data: filteredBottles,
        total: filteredBottles.length,
        page: 1,
        totalPages: 1
      };
      
      console.log('🌐 API - Données archives finales:', paginatedData);
      return paginatedData;
    } catch (error: any) {
      console.error('🌐 API - Erreur getArchivedBottles:', error);
      throw error;
    }
  },

  createBottleWithImage: async (bottle: CreateBottleDto, image?: File, reference?: string): Promise<Bottle> => {
    try {
      const formData = new FormData();
      
      // Ajouter les champs de la bouteille (format identique à l'app mobile)
      formData.append('reference', reference || '');
      formData.append('name', bottle.name);
      formData.append('type', (BOTTLE_TYPE_REVERSE_MAPPING[bottle.type] || 1).toString());
      formData.append('annee', bottle.year?.toString() || '');
      formData.append('description', bottle.description || '');
      formData.append('note', bottle.rating?.toString() || '0');
      formData.append('occasion', bottle.isGift ? '1' : '0');
      formData.append('share', bottle.isPrivate ? 'false' : 'true');
      
      // Ajouter l'image si présente : créer un Blob avec le Base64 comme contenu texte
      if (image) {
        const base64 = await fileToBase64(image);
        // Créer un Blob avec le Base64 comme contenu texte (pas de fichier binaire)
        const base64Blob = new Blob([base64], { type: 'text/plain' });
        formData.append('file', base64Blob, 'file');
      }
      
      const { data } = await apiClient.post<BackendResponse<string>>(
        API_CONFIG.ENDPOINTS.BOTTLES.CREATE_WITH_IMAGE, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      // Gérer la réponse exactement comme l'app mobile
      if (data.status === '200' && data.content === 'CREATED') {
        // Reconstituer l'objet bouteille comme fait l'app mobile
        const createdBottle: Bottle = {
          id: reference || '',
          name: bottle.name,
          type: bottle.type,
          year: bottle.year,
          description: bottle.description,
          rating: bottle.rating || 0,
          isGift: bottle.isGift,
          isPrivate: bottle.isPrivate,
          imageUrl: undefined, // Sera mise à jour par le backend
          createdAt: new Date(),
          archivedAt: undefined,
          archiveComment: undefined,
          isArchived: false,
          userId: '0' // Sera mis à jour lors du refresh de la liste
        };
        return createdBottle;
      } else if (data.content === 'EXIST_ALREADY') {
        throw new Error('EXIST_ALREADY');
      } else if (data.content === 'TAG_NOT_EXIST') {
        throw new Error('TAG_NOT_RECOGNIZED');
      } else {
        throw new Error(data.message || `Erreur lors de la création: ${data.content}`);
      }
    } catch (error: any) {
      console.error('🌐 API - Erreur createBottleWithImage:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },

  uploadBottleImage: async (bottleId: string, image: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image', image);
    
    const { data } = await apiClient.post<{ imageUrl: string }>(
      `${API_CONFIG.ENDPOINTS.BOTTLES.GET(bottleId)}/image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return data.imageUrl;
  }
};